<template>
  <div class="wait-chk-page">
    <!-- 顶部查询与操作栏（对标 stk_check 风格） -->
    <header class="query-header" aria-label="备料确认">
      <button type="button" class="back-button" aria-label="返回功能首页" :disabled="busy" @click="onClickLeft">
        <van-icon name="arrow-left" size="20" aria-hidden="true" />
      </button>
      <form class="search-form" role="search" @submit.prevent="onSearch">
        <div class="search-input">
          <van-icon name="search" size="14" aria-hidden="true" />
          <input
            v-model="v_search"
            :disabled="busy"
            aria-label="搜索销售订单、产品编码或请购单号"
            placeholder="销售订单 / 产品编码 / 请购单号"
            type="search"
            enterkeyhint="search"
            @keydown.enter.prevent="onSearch"
          />
          <button v-if="v_search" type="button" class="clear-button" aria-label="清空搜索" @click="clearSearch">
            <van-icon name="cross" size="16" />
          </button>
        </div>
        <button type="submit" class="header-action-button" aria-label="搜索" :disabled="busy">
          <van-icon name="search" size="16" aria-hidden="true" />
        </button>
        <button type="button" class="header-action-button" aria-label="刷新" :disabled="listLoading || busy" @click="onSearch">
          <van-icon name="replay" size="16" :class="{ 'is-spinning': listLoading }" aria-hidden="true" />
        </button>
      </form>
    </header>

    <!-- 主列表区域 -->
    <main id="wait-chk-list" ref="listContent" class="list-content" :aria-busy="listLoading">
      <div v-if="listLoading && !allData.length" class="state-panel" role="status">
        <van-loading size="24px" />
        <span>正在查询未确认单据…</span>
      </div>
      <div v-else-if="!tabData.length" class="state-panel">
        <van-icon name="description" size="40" color="#999999" />
        <p>暂无待确认请购单据</p>
      </div>
      <template v-else>
        <article
          v-for="(item, index) in tabData"
          :key="item.IDSEQ"
          class="chk-card"
          :aria-label="'请购单 ' + item.IDNO"
        >
          <!-- 卡片头部：左侧序号、单号与分类，右侧状态标签 -->
          <header class="card-header">
            <div class="card-title-group">
              <span class="card-seq">{{ (currentPage - 1) * rowSize + index + 1 }}</span>
              <span class="card-tag">请购单</span>
              <h2 class="card-idno">{{ item.IDNO }}</h2>
            </div>
            <span class="status-tag is-pending">{{ item.ISCHK === 'Y' ? '已确认' : '未确认' }}</span>
          </header>

          <!-- 单据核心信息区：自适应网格，杜绝任何溢出与挤压重叠 -->
          <dl class="document-info">
            <div>
              <dt>请购日期</dt>
              <dd>{{ item.REQDTTM || '—' }}</dd>
            </div>
            <div>
              <dt>订单编码</dt>
              <dd>{{ item.ORDNO || '—' }}</dd>
            </div>
            <div class="full-row">
              <dt>关联产品</dt>
              <dd class="highlight-text">{{ item.PRDNO || '—' }}</dd>
            </div>
            <div class="full-row">
              <dt>产品名称</dt>
              <dd>{{ item.PRDNM || '—' }}</dd>
            </div>
            <div class="full-row count-and-expand-row">
              <div class="count-item-wrap">
                <dt>确认项数</dt>
                <dd class="count-tag-wrap">
                  <span class="count-badge">{{ item.ROWCNT || 0 }}({{ item.OKCNT || 0 }})</span>
                </dd>
              </div>
              <!-- “展开”按钮放置在确认项数同行右边 -->
              <button
                type="button"
                class="toggle-expand-btn"
                :aria-expanded="isExpanded(item)"
                @click="toggleExpand(item)"
              >
                <span>{{ isExpanded(item) ? '收起' : '展开' }}</span>
                <van-icon :name="isExpanded(item) ? 'arrow-up' : 'arrow-down'" />
              </button>
            </div>
          </dl>

          <!-- 就地展开的预备料清单区域（只展示未备料） -->
          <section v-if="isExpanded(item)" class="expanded-details" :aria-label="'请购单 ' + item.IDNO + ' 预备料明细'">
            <div class="details-header">
              <span class="details-title">未备料物料清单</span>
              <span class="details-badge">待备料 {{ unconfirmedDetails(item).length }} 项</span>
            </div>

            <!-- 明细加载状态 -->
            <div v-if="dtlLoadingMap[item.IDSEQ]" class="state-panel-inline">
              <van-loading size="20px">正在加载预备料明细…</van-loading>
            </div>

            <!-- 空未备料提示 -->
            <div v-else-if="!unconfirmedDetails(item).length" class="empty-dtl-notice">
              <van-icon name="passed" color="#FF4000" size="18" />
              <span>当前单据物料已全部完成备料确认</span>
            </div>

            <!-- 未备料物料子卡片列表 -->
            <div v-else class="dtl-card-list">
              <article
                v-for="(row, rowIdx) in unconfirmedDetails(item)"
                :key="row.IDSEQ + '_' + row.LSTSEQ"
                class="dtl-item-card"
              >
                <!-- 物料行头部 -->
                <header class="dtl-heading">
                  <span class="dtl-seq-badge">{{ rowIdx + 1 }}</span>
                  <div class="dtl-mat-info">
                    <div class="dtl-mat-title">
                      <span class="dtl-mat-no">{{ row.MATNO || '—' }}</span>
                      <span class="dtl-mat-nm">{{ row.MATNM || '未命名物料' }}</span>
                    </div>
                    <p class="dtl-mat-spec">{{ row.MATDESC || '暂无规格' }}</p>
                  </div>
                </header>

                <!-- 数据网格：锁库数量（标红粗体）、可用数量、库存数量、仓库名称 -->
                <div class="dtl-meta-grid">
                  <div class="meta-item locked-item">
                    <span class="meta-label">锁库数量</span>
                    <span class="meta-value locked-val">{{ row.LOCKQTY == null || row.LOCKQTY === '' ? '0' : row.LOCKQTY }}</span>
                  </div>
                  <div class="meta-item">
                    <span class="meta-label">可用数量</span>
                    <span class="meta-value">{{ row.OKQTY == null || row.OKQTY === '' ? '—' : row.OKQTY }}</span>
                  </div>
                  <div class="meta-item">
                    <span class="meta-label">库存数量</span>
                    <span class="meta-value">{{ row.STKQTY == null || row.STKQTY === '' ? '0' : row.STKQTY }}</span>
                  </div>
                  <div class="meta-item">
                    <span class="meta-label">仓库名称</span>
                    <span class="meta-value">{{ row.STKNM || '—' }}</span>
                  </div>
                </div>

                <!-- 紧凑表单输入区域 -->
                <div class="dtl-input-grid">
                  <div class="input-cell">
                    <label :for="'chkqty-' + row.IDSEQ + '-' + row.LSTSEQ" class="input-cell-label">确认数量</label>
                    <div class="input-with-unit">
                      <input
                        :id="'chkqty-' + row.IDSEQ + '-' + row.LSTSEQ"
                        v-model="row.CHKQTY"
                        type="number"
                        min="0"
                        step="any"
                        placeholder="请输入数量"
                        class="cell-input"
                        :disabled="isPreparing || isPrinting"
                        @input="markItemUnconfirmed(row)"
                      />
                      <span v-if="row.UNITNM" class="cell-unit">{{ row.UNITNM }}</span>
                    </div>
                  </div>
                  <div class="input-cell">
                    <label :for="'plcnm-' + row.IDSEQ + '-' + row.LSTSEQ" class="input-cell-label">仓位名称</label>
                    <input
                      :id="'plcnm-' + row.IDSEQ + '-' + row.LSTSEQ"
                      v-model="row.PLCNM"
                      type="text"
                      placeholder="仓位名称"
                      class="cell-input"
                      :disabled="isPreparing || isPrinting"
                      @input="markItemUnconfirmed(row)"
                    />
                  </div>
                </div>

                <!-- 行操作按钮：确认备料 -->
                <div class="dtl-action-row">
                  <van-button
                    size="small"
                    plain
                    type="info"
                    class="line-confirm-btn"
                    :disabled="isPreparing || isPrinting"
                    @click="prtRec(row, false, item)"
                  >
                    确认备料
                  </van-button>
                </div>
              </article>
            </div>
          </section>

          <!-- 卡片底部操作按钮区域（从右往左：全部确认） -->
          <footer class="card-footer-actions">
            <van-button
              type="primary"
              size="small"
              class="action-btn confirm-btn"
              :disabled="cfmBool || busy"
              @click="stfgRec(item, 'Y')"
            >
              全部确认
            </van-button>
          </footer>
        </article>
      </template>
    </main>

    <!-- 分页栏 -->
    <footer v-if="allData.length > rowSize" class="pagination-footer">
      <van-pagination
        v-model="currentPage"
        :total-items="v_total"
        :items-per-page="rowSize"
        :show-page-size="5"
        force-ellipses
        @change="changePage"
      >
        <template #prev-text>
          <van-icon name="arrow-left" />
        </template>
        <template #next-text>
          <van-icon name="arrow" />
        </template>
        <template #page="{ text }">{{ text }}</template>
      </van-pagination>
    </footer>

    <!-- 返回顶部按钮 -->
    <div v-show="topShow" class="go_top_btn" @click="goTop">
      <van-icon name="arrow-up" size="24px" color="#235EFF" />
    </div>

    <!-- 物料二维码打印预览弹窗 -->
    <van-popup
      v-model="showPreview"
      :close-on-click-overlay="!isPrinting"
      :style="{ height: '100%', width: '100%', background: '#f5f5f5' }"
    >
      <div style="display:flex;flex-direction:column;height:100%">
        <van-nav-bar title="打印预览">
          <template #left>
            <button type="button" class="label-preview-action" :disabled="isPrinting" @click="showPreview = false">取消</button>
          </template>
          <template #right>
            <button type="button" class="label-preview-action" :disabled="isPrinting" @click="confirmPrint()">
              {{ isPrinting ? '打印中…' : '确认打印' }}
            </button>
          </template>
        </van-nav-bar>
        <div style="flex:1;overflow-y:auto;text-align:center;padding:16px 12px">
          <img :src="previewUrl" class="label-preview-image" alt="物料二维码标签" />
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import { getDate } from '@/api/tools'
import {
  isSupported as btSupported,
  getUnsupportedReason,
  printMaterialBarcode,
  renderPreview as renderMaterialPreview,
  getPreviewTopMarginMm,
} from '@/api/materialBarcodePrint'

export default {
  name: 'WaitChk',
  data() {
    return {
      v_title: '备料确认',
      v_search: '',
      listLoading: false,
      busy: false,

      // 用户信息与权限
      v_usernm: this.$cookies.get('v_usernm'),
      v_username: this.$cookies.get('v_telno'),
      v_corpid: this.$cookies.get('v_corpid'),
      cfmBool: false,
      editBool: false,
      addBool: false,
      cancelBool: false,

      // 主单数据
      allData: [],
      tabData: [],
      currentPage: 1,
      v_total: 0,
      rowSize: 10,

      // 展开状态与明细数据容器
      expandedIds: [],
      detailsMap: {},
      dtlLoadingMap: {},

      // 打印相关状态
      showPreview: false,
      previewUrl: '',
      pendingLabel: null,
      isPreparing: false,
      isPrinting: false,
      printCopies: 1,
      previewTopMarginMm: 0,

      // 滚动与返回顶部
      topShow: false,
      scroll: 0,
    }
  },
  mounted() {
    window.addEventListener('scroll', this.windowScroll, true)
    this.initData()
    this.onSearch()
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.windowScroll, true)
  },
  methods: {
    // 页面跳转返回
    onClickLeft() {
      if (!this.busy) {
        this.$router.push('/features')
      }
    },

    // 清空搜索内容并触发检索
    clearSearch() {
      this.v_search = ''
      this.onSearch()
    },

    // 查询主单：强制锁定未确认 ischk='N'
    async onSearch() {
      if (this.busy) return
      this.listLoading = true
      let v_cond = ''
      if (this.v_search && this.v_search.trim()) {
        const v_val = this.v_search.trim().toUpperCase()
        v_cond = " and (saleno like '%" + v_val + "%' or idno like '%" + v_val + "%' or staff like '%" + v_val + "%' or prdno like '%" + v_val + "%' or ordno like '%" + v_val + "%' )"
      }
      // 强制在 SQL 条件中锁定未确认条件，并按 reqdttm 倒序排列
      v_cond = " and ischk='N' " + (v_cond ? v_cond : '') + ' order by reqdttm desc'

      try {
        const res = await this.$axios({
          method: 'post',
          url: 'xuper_auth/getBaseData',
          data: {
            p_key: 'stfg',
            p_value: 'Y',
            p_cond: v_cond,
            p_table: 'V_PO_REQ_LOCK',
          },
        })
        if (res && res.code === '200') {
          this.allData = Array.isArray(res.result) ? res.result : []
          this.currentPage = 1
          this.initPage()
        } else {
          this.$dialog.alert({
            title: '提示',
            message: (res && res.result) || '查询数据失败',
            theme: 'round-button',
          })
        }
      } catch (err) {
        this.$toast.fail('查询失败：' + (err.message || '网络异常'))
      } finally {
        this.listLoading = false
      }
    },

    // 分页初始化
    initPage() {
      this.v_total = this.allData.length
      if (this.allData.length <= this.rowSize) {
        this.tabData = this.allData
      } else {
        const _start = (this.currentPage - 1) * this.rowSize
        const _end = _start + this.rowSize
        this.tabData = this.allData.slice(_start, _end)
      }
    },

    // 分页切换
    changePage(index) {
      this.currentPage = index
      const _start = (index - 1) * this.rowSize
      const _end = index * this.rowSize
      this.tabData = this.allData.slice(_start, _end)
    },

    // 展开状态判断
    isExpanded(item) {
      return this.expandedIds.includes(String(item.IDSEQ))
    },

    // 切换卡片展开/收起
    toggleExpand(item) {
      const id = String(item.IDSEQ)
      const idx = this.expandedIds.indexOf(id)
      if (idx >= 0) {
        this.expandedIds.splice(idx, 1)
      } else {
        this.expandedIds.push(id)
        if (!this.detailsMap[id] || !this.detailsMap[id].length) {
          this.getDtlList(item.IDSEQ)
        }
      }
    },

    // 获取单据明细数据
    async getDtlList(v_idseq) {
      const id = String(v_idseq)
      this.$set(this.dtlLoadingMap, id, true)
      try {
        const res = await this.$axios({
          method: 'post',
          url: 'xuper_auth/getBaseData',
          data: {
            p_key: 'idseq',
            p_value: v_idseq,
            p_table: 'V_POREQDTL_LOCK',
            p_cond: ' order by matno ',
          },
        })
        if (res && res.code === '200' && Array.isArray(res.result)) {
          const list = res.result.map(row => {
            const emptyQty = row.CHKQTY == null || String(row.CHKQTY).trim() === ''
            return {
              ...row,
              CHKQTY: emptyQty && row.CHKSTFG !== 'Y' ? (row.LOCKQTY == null ? '' : row.LOCKQTY) : row.CHKQTY,
              CHKSTFG: row.CHKSTFG === 'Y' ? 'Y' : 'N',
            }
          })
          this.$set(this.detailsMap, id, list)
        } else {
          this.$dialog.alert({
            title: '提示',
            message: (res && res.result) || '获取明细失败',
            theme: 'round-button',
          })
        }
      } catch (err) {
        this.$toast.fail('明细加载失败：' + (err.message || '网络异常'))
      } finally {
        this.$set(this.dtlLoadingMap, id, false)
      }
    },

    // 过滤出未备料物料（CHKSTFG !== 'Y'）
    unconfirmedDetails(item) {
      const list = this.detailsMap[String(item.IDSEQ)] || []
      return list.filter(row => row.CHKSTFG !== 'Y')
    },

    // 标记物料行状态
    markItemUnconfirmed(row) {
      this.$set(row, 'CHKSTFG', 'N')
    },

    // 单项物料确认备料
    async prtRec(row, shouldPrint = false, parentDoc = null) {
      if (this.isPreparing || this.isPrinting) return

      const copies = shouldPrint ? (row.printCopies === undefined ? 1 : Number(row.printCopies)) : 0
      if (shouldPrint && (!Number.isInteger(copies) || copies < 1 || copies > 100)) {
        this.$dialog.alert({ title: '打印份数', message: '请输入 1 到 100 的整数' })
        return
      }

      const v_code = row.MATNO === null || row.MATNO === undefined ? '' : String(row.MATNO)
      if (copies > 0 && !v_code.trim()) {
        this.$dialog.alert({ title: '提示', message: '该明细没有物料编码，无法生成二维码', theme: 'round-button' })
        return
      }

      if (row.CHKQTY === null || row.CHKQTY === undefined || String(row.CHKQTY).trim() === '') {
        this.$dialog.alert({ title: '提示', message: '请输入确认数量', theme: 'round-button' })
        return
      }

      const v_qty = Number(row.CHKQTY)
      if (!Number.isFinite(v_qty) || v_qty < 0) {
        this.$dialog.alert({ title: '提示', message: '确认数量必须为大于或等于零的数字', theme: 'round-button' })
        return
      }

      const v_label = {
        matnm: row.MATNM || '',
        specnm: row.MATDESC || '',
        stknm: row.STKNM || '',
        plcnm: row.PLCNM || '',
        code: v_code,
      }

      const quoteValue = val => String(val === null || val === undefined ? '' : val).replace(/'/g, "''")
      const v_cond =
        " set chkqty='" +
        v_qty +
        "',STKNO='" +
        quoteValue(row.STKNO) +
        "',STKNM='" +
        quoteValue(v_label.stknm) +
        "',plcnm='" +
        quoteValue(v_label.plcnm) +
        "',chkstfg='Y' "

      this.isPreparing = true
      this.pendingLabel = null
      this.previewUrl = ''
      let stage = '确认信息失败'
      const loadingToast = this.$toast.loading({ message: '确认备料中...', duration: 0, forbidClick: true })

      try {
        const res = await this.$axios({
          method: 'post',
          url: 'xuper_auth/updateRec',
          data: {
            p_key: 'idseq',
            p_value: row.IDSEQ,
            p_key2: 'lstseq',
            p_value2: row.LSTSEQ,
            p_table: 'po_reqdtl',
            p_cond: v_cond,
          },
        })
        if (!res || String(res.code) !== '200') {
          throw new Error(res && typeof res.result === 'string' ? res.result : '确认信息失败')
        }

        // 成功后置为已备料状态，自动从未备料清单移出
        this.$set(row, 'CHKSTFG', 'Y')

        // 更新父单据中已确认项数计数
        if (parentDoc) {
          const okNum = Number(parentDoc.OKCNT) || 0
          this.$set(parentDoc, 'OKCNT', okNum + 1)
        }

        if (copies === 0) {
          this.showPreview = false
          this.$toast.success('备料确认成功')
          return
        }

        stage = '生成预览失败'
        loadingToast.message = '生成预览...'
        const url = await renderMaterialPreview(v_label)
        this.previewTopMarginMm = await getPreviewTopMarginMm(url)
        this.pendingLabel = v_label
        this.previewUrl = url
        this.printCopies = copies
        this.showPreview = true
      } catch (err) {
        this.$dialog.alert({ title: '提示', message: stage + '：' + ((err && err.message) || '未知错误') })
      } finally {
        loadingToast.clear()
        this.isPreparing = false
      }
    },

    // 确认打印（蓝牙）
    async confirmPrint(options) {
      if (this.isPrinting) return
      const v_label = this.pendingLabel
      if (!v_label) return
      const copies = Number(this.printCopies)
      if (!Number.isInteger(copies) || copies < 1 || copies > 100) {
        this.$dialog.alert({ title: '打印份数', message: '请输入 1 到 100 的整数' })
        return
      }
      const settings = options || {}
      if (settings.topShiftMm !== undefined && settings.topShiftMm !== 0) {
        this.$dialog.alert({ title: '打印位置', message: 'CC4 回退走纸补偿已停用，请刷新页面' })
        return
      }
      if (!btSupported()) {
        this.$dialog.alert({ title: '不支持蓝牙打印', message: getUnsupportedReason() })
        return
      }
      this.isPrinting = true
      const pToast = this.$toast.loading({ message: '正在打印...', duration: 0, forbidClick: true })
      let sent = 0
      try {
        for (let i = 0; i < copies; i++) {
          pToast.message = '正在发送 ' + (i + 1) + '/' + copies + ' 份…'
          await printMaterialBarcode(v_label, { previewUrl: this.previewUrl })
          sent++
          if (i < copies - 1) {
            pToast.message = '已发送 ' + sent + '/' + copies + ' 份，等待打印机定位…'
            await new Promise(resolve => setTimeout(resolve, 2000))
          }
        }
      } catch (err) {
        if (!err || err.name !== 'NotFoundError') {
          this.$dialog.alert({
            title: '打印失败',
            message: '已发送 ' + sent + '/' + copies + ' 份，打印已停止。' + ((err && err.message) || '连接打印机失败'),
          })
        }
        return
      } finally {
        pToast.clear()
        this.isPrinting = false
      }
      if (!settings.keepPreview) this.showPreview = false
      this.$toast.success('已发送 ' + sent + ' 份，请确认纸面打印结果')
    },

    // 全部确认（原审核操作，更名为全部确认）
    async stfgRec(item, val) {
      if (this.busy || this.cfmBool) return

      try {
        // 先检查是否存在未录入确认数量的明细记录
        const resChk = await this.$axios({
          method: 'post',
          url: 'xuper_auth/getBaseData',
          data: {
            p_key: 'idseq',
            p_value: item.IDSEQ,
            p_table: 'V_POREQDTL_LOCK',
            p_cond: ' AND CHKQTY IS NULL ',
          },
        })
        if (resChk && resChk.result && resChk.result.length > 0 && val === 'Y') {
          this.$dialog.alert({
            title: '提示',
            message: '明细记录存在未录确认数量，无法全部确认',
            theme: 'round-button',
          })
          if (!this.isExpanded(item)) this.toggleExpand(item)
          return
        }

        await this.$dialog.confirm({
          title: '全部确认提示',
          message: '确定要将请购单 ' + item.IDNO + ' 进行全部确认吗？',
          confirmButtonColor: '#235EFF',
          confirmButtonText: '确认',
          cancelButtonText: '取消',
        })

        const v_chkdttm = getDate('full')
        const v_chkuser = this.v_usernm || ''
        const v_cond = " set ischk='Y',appuser='" + v_chkuser + "',appdttm='" + v_chkdttm + "'"

        const loadingToast = this.$toast.loading({
          message: '提交确认中...',
          forbidClick: true,
          duration: 0,
        })

        try {
          const res = await this.$axios({
            method: 'post',
            url: 'xuper_auth/updateRec',
            data: {
              p_key: 'idseq',
              p_value: item.IDSEQ,
              p_table: 'po_req',
              p_cond: v_cond,
            },
          })
          if (res && String(res.code) === '200') {
            this.$toast.success('全部确认成功')
            // 移除展开状态并重新加载未确认单据列表
            const expIdx = this.expandedIds.indexOf(String(item.IDSEQ))
            if (expIdx >= 0) this.expandedIds.splice(expIdx, 1)
            await this.onSearch()
          } else {
            throw new Error((res && res.result) || '全部确认失败')
          }
        } finally {
          loadingToast.clear()
        }
      } catch (err) {
        if (err !== 'cancel') {
          this.$dialog.alert({
            title: '操作失败',
            message: err.message || '全部确认出现异常，请重试',
          })
        }
      }
    },

    // 页面初始化与用户权限
    initData() {
      // 获取功能模块配置标题
      if (this.$route.query.path) {
        this.$axios({
          method: 'post',
          url: 'xuper_auth/getBaseData',
          data: {
            p_key: 'isqry',
            p_value: 'M',
            p_key2: 'frmurl',
            p_value2: this.$route.query.path,
            p_table: 'V_Sys_form',
          },
        }).then(res => {
          if (res && res.result && res.result.length > 0) {
            this.v_title = res.result[0].CFRMNM || '备料确认'
          }
        })
      }

      // 获取当前用户权限
      this.$axios({
        method: 'post',
        url: 'xuper_auth/getBaseData',
        data: {
          p_key: 'userno',
          p_value: this.v_username,
          p_key2: 'frmidseq',
          p_value2: '419828',
          p_table: 'v_sys_user_priv',
        },
      }).then(res => {
        if (res && res.result && res.result.length > 0) {
          const row = res.result[0]
          this.addBool = row.ISADD === 'N'
          this.editBool = row.ISUPD === 'N'
          this.cfmBool = row.ISCFM === 'N'
          this.cancelBool = row.ISCANCEL === 'N'
        }
      })
    },

    // 监听滚动与返回顶部
    windowScroll() {
      const container = this.$refs.listContent
      const scrollPos = container ? container.scrollTop : document.documentElement.scrollTop || document.body.scrollTop
      this.topShow = scrollPos > 240
    },

    // 平滑返回顶部
    goTop() {
      const container = this.$refs.listContent
      if (container) {
        container.scrollTo({ top: 0, behavior: 'smooth' })
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import '@/assets/css/scss/variables.scss';

.wait-chk-page {
  height: 100vh;
  height: 100dvh;
  display: flex;
  flex-direction: column;
  background: #f7f8fa;
  color: $text-primary;
  font: 14px/1.45 $font-family-base;
}

// 头部顶栏（对标 stk_check）
.query-header {
  flex: none;
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 48px;
  padding: 4px 8px;
  background: white;
  border-bottom: 1px solid #ebedf0;
}

.back-button {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 0;
  background: transparent;
  color: $text-primary;
  &:disabled {
    color: $text-tertiary;
  }
}

.search-form {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  gap: 6px;
}

.search-input {
  min-width: 0;
  flex: 1;
  height: 32px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 10px;
  border: 1px solid $border-color;
  border-radius: 16px;
  background: #f7f8fa;
  color: $text-secondary;

  &:focus-within {
    border-color: $primary-color;
    background: white;
  }

  input {
    width: 0;
    min-width: 0;
    flex: 1;
    height: 30px;
    outline: none;
    border: none;
    background: transparent;
    font: inherit;
    font-size: 13px;
    color: $text-primary;
  }

  input::-webkit-search-cancel-button {
    display: none;
  }
}

.clear-button {
  width: 28px;
  height: 28px;
  flex: none;
  border: 0;
  background: transparent;
  color: $text-secondary;
}

.header-action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex: none;
  padding: 0;
  border: 1px solid #ebedf0;
  border-radius: 6px;
  background: white;
  color: $text-primary;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);

  &:active {
    background: #f0f2f5;
  }

  &:disabled {
    color: $text-tertiary;
    box-shadow: none;
  }
}

.is-spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  100% {
    transform: rotate(360deg);
  }
}

// 主列表滚动区域
.list-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 8px;
}

.state-panel {
  min-height: 180px;
  padding: 28px 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 12px;
  text-align: center;
  color: $text-secondary;
}

// 主单卡片样式（紧凑型）
.chk-card {
  max-width: 760px;
  margin: 0 auto 6px;
  padding: 8px 10px;
  border: 1px solid #ebedf0;
  border-radius: 6px;
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  margin-bottom: 6px;
}

.card-title-group {
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
  flex: 1;
}

.card-seq {
  flex: none;
  min-width: 18px;
  height: 18px;
  line-height: 18px;
  padding: 0 4px;
  border-radius: 9px;
  font-size: 11px;
  font-weight: 600;
  text-align: center;
  background: #f0f2f5;
  color: $text-secondary;
}

.card-tag {
  flex: none;
  padding: 0 4px;
  border-radius: 3px;
  font-size: 10px;
  line-height: 16px;
  background: #edf2ff;
  color: $primary-color;
  font-weight: 500;
}

.card-idno {
  min-width: 0;
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  overflow-wrap: anywhere;
  word-break: break-all;
}

// 头部状态标签（紧凑型）
.status-tag {
  flex: none;
  border-radius: 3px;
  padding: 0 5px;
  font-size: 11px;
  line-height: 18px;
  font-weight: 500;
}
.is-pending {
  background: #FFF0ED;
  color: #FF4000;
  border: 1px solid #FFD6CC;
}
.is-audited {
  background: #EDF2FF;
  color: $primary-color;
}

// 展开/收起按钮（紧凑微胶囊）
.toggle-expand-btn {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  height: 22px;
  padding: 0 6px;
  border: 1px solid #dcdfe6;
  border-radius: 3px;
  background: #f8f9fa;
  color: $primary-color;
  font-size: 11px;
  font-weight: 500;
  flex: none;

  &:active {
    background: #ebedf0;
  }
}

// 单据核心信息区（紧凑网格）
.document-info {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 2px 8px;
  margin: 0;
  font-size: 12px;
  line-height: 19px;

  > div {
    display: grid;
    grid-template-columns: 52px minmax(0, 1fr);
    align-items: baseline;
    gap: 4px;
    min-width: 0;
  }

  dt {
    font-size: 11.5px;
    color: $text-secondary;
    white-space: nowrap;
  }

  dd {
    margin: 0;
    overflow-wrap: anywhere;
    word-break: break-all;
    color: $text-primary;
    font-size: 12px;
  }
}

.full-row {
  grid-column: 1 / -1;
}

.count-and-expand-row {
  display: flex !important;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  margin-top: 1px;
}

.count-item-wrap {
  display: grid;
  grid-template-columns: 52px minmax(0, 1fr);
  align-items: baseline;
  gap: 4px;
  min-width: 0;
}

.highlight-text {
  font-weight: 600;
  color: #103b9a;
}

.count-tag-wrap {
  display: flex;
  align-items: center;
}

.count-badge {
  display: inline-block;
  padding: 0 5px;
  border-radius: 3px;
  background: #f0f5ff;
  color: $primary-color;
  font-weight: 600;
  font-size: 11px;
  line-height: 16px;
}

// 就地展开容器（紧凑排版）
.expanded-details {
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px dashed #dcdfe6;
}

.details-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 5px;
}

.details-title {
  font-size: 12px;
  font-weight: 600;
  color: $text-primary;
}

.details-badge {
  font-size: 11px;
  color: $text-secondary;
}

.state-panel-inline {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 0;
  color: $text-secondary;
  font-size: 12px;
}

.empty-dtl-notice {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px 6px;
  background: #f8fafc;
  border-radius: 4px;
  color: $text-secondary;
  font-size: 11.5px;
}

// 未备料物料子卡片
.dtl-card-list {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.dtl-item-card {
  padding: 6px 8px;
  border: 1px solid #e2e8f0;
  border-radius: 5px;
  background: #f8fafc;
}

.dtl-heading {
  display: flex;
  gap: 5px;
  align-items: flex-start;
}

.dtl-seq-badge {
  width: 16px;
  height: 16px;
  line-height: 16px;
  flex: none;
  border-radius: 2px;
  text-align: center;
  background: #edf2ff;
  color: $primary-color;
  font-size: 10px;
  font-weight: 600;
  margin-top: 1px;
}

.dtl-mat-info {
  flex: 1;
  min-width: 0;
}

.dtl-mat-title {
  display: flex;
  flex-wrap: wrap;
  gap: 3px 5px;
  align-items: baseline;
}

.dtl-mat-no {
  font-size: 12px;
  font-weight: 600;
  color: #1a202c;
  overflow-wrap: anywhere;
  word-break: break-all;
}

.dtl-mat-nm {
  font-size: 12px;
  color: $text-primary;
  overflow-wrap: anywhere;
  word-break: break-all;
}

.dtl-mat-spec {
  margin: 1px 0 0;
  font-size: 11px;
  color: $text-secondary;
  line-height: 15px;
  overflow-wrap: anywhere;
  word-break: break-all;
}

// 数据网格
.dtl-meta-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 2px 8px;
  margin: 4px 0;
  padding: 4px 6px;
  background: white;
  border-radius: 4px;
  border: 1px solid #edf2f7;
}

.meta-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 11.5px;
  line-height: 18px;
  min-width: 0;
}

.meta-label {
  color: $text-secondary;
  white-space: nowrap;
}

.meta-value {
  font-weight: 500;
  color: $text-primary;
  overflow-wrap: anywhere;
  word-break: break-all;
  font-variant-numeric: tabular-nums;
}

.locked-val {
  font-weight: 700;
  color: #e53e3e;
}

// 紧凑表单输入
.dtl-input-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 4px 6px;
  margin: 4px 0;
}

.input-cell {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.input-cell-label {
  font-size: 10.5px;
  color: $text-secondary;
}

.cell-input {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  height: 26px;
  padding: 0 5px;
  border: 1px solid $border-color;
  border-radius: 3px;
  background: white;
  font-size: 11.5px;
  color: $text-primary;
  outline: none;

  &:focus {
    border-color: $primary-color;
  }
}

.input-with-unit {
  position: relative;
  display: flex;
  align-items: center;

  .cell-input {
    padding-right: 28px;
  }
}

.cell-unit {
  position: absolute;
  right: 5px;
  font-size: 10.5px;
  color: $text-tertiary;
  pointer-events: none;
}

// 行操作按钮
.dtl-action-row {
  display: flex;
  justify-content: flex-end;
  margin-top: 4px;
}

.line-confirm-btn {
  height: 24px;
  line-height: 22px;
  padding: 0 8px;
  border-radius: 3px;
  font-size: 11px;
}

// 卡片底部操作按钮区域（从右往左，对齐采购审核 po_cfm 规范）
.card-footer-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid #f0f2f5;
}

.action-btn {
  flex: none;
  min-width: 66px;
  height: 26px;
  line-height: 24px;
  padding: 0 12px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 13px;
  color: #fff !important;
}

.confirm-btn {
  background: #1ECD84 !important;
  border-color: #1ECD84 !important;

  &:active {
    background: #12A66A !important;
    border-color: #12A66A !important;
  }

  &.van-button--disabled {
    background: $color-disabled !important;
    border-color: $color-disabled !important;
    color: #fff !important;
  }
}

// 分页固定底栏
.pagination-footer {
  flex: none;
  background: white;
  border-top: 1px solid #ebedf0;
  padding: 4px 0;
}

// 返回顶部按钮
.go_top_btn {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #fff;
  border-radius: 50%;
  border: 1px solid #dcdcdc;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  position: fixed;
  right: 16px;
  bottom: 60px;
  z-index: 99;
  cursor: pointer;
}

// 打印预览
.label-preview-action {
  border: 0;
  background: transparent;
  color: $primary-color;
  font-size: 14px;
  min-height: 44px;
  padding: 0 8px;

  &:disabled {
    color: $text-tertiary;
  }
}

.label-preview-image {
  display: block;
  margin: 0 auto;
  width: 560px;
  max-width: 100%;
  height: auto;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}
</style>
